import React, { useContext, useEffect } from "react";
import { RulesContext } from "../../RulesContext";
import { COND, OP } from "../../misc/RuleObjectTypes";
import ConditionOperator from "./ConditionOperator";
import Condition from "./Condition";
import NestedCondition from "./NestedCondition";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Lightweight replacement for array-move's mutating helper, preserving the
// exact in-place reorder semantics the onSortEnd logic below relies on.
const arrayMoveMutate = (array, from, to) => {
  const startIndex = from < 0 ? array.length + from : from;
  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = to < 0 ? array.length + to : to;
    const [item] = array.splice(from, 1);
    array.splice(endIndex, 0, item);
  }
};

const RuleBody = ({ ruleBody }) => {
  const { ruleDispatch, ruleState } = useContext(RulesContext);
  const { rule } = ruleState;

  useEffect(() => {
    if (ruleBody) {
      ruleDispatch({
        type: "UPDATE_RULE",
        updatedRule: ruleBody.rule,
      });
    }
  }, [ruleBody]);

  const setPleaseSelect = (arr, index, attrString) => {
    arr[index][attrString] = "Please Select";
  };
  const onSortEnd = ({ oldIndex, newIndex }) => {
    let final = [...rule];
    const left = "leftAttrID";
    const right = "rightAttrID";

    if (final[newIndex].type === "Operator") {
      arrayMoveMutate(final, oldIndex, newIndex);
      arrayMoveMutate(final, oldIndex, newIndex);
    } else if (oldIndex > newIndex) {
      arrayMoveMutate(final, oldIndex, newIndex);
      arrayMoveMutate(final, oldIndex, newIndex + 1);
    } else if (oldIndex < newIndex) {
      arrayMoveMutate(final, oldIndex, newIndex);
      arrayMoveMutate(final, oldIndex, newIndex - 1);
    } else {
      arrayMoveMutate(final, oldIndex, newIndex);
    }

    if (!Array.isArray(final[newIndex])) {
      final[newIndex]["position"] = newIndex.toString();
    }

    if (!Array.isArray(final[oldIndex])) {
      final[oldIndex]["position"] = oldIndex.toString();
    }
    
    final.map((item, index) => {
      if (Array.isArray(item)) {
        item.map((subItem, subIndex) => {
          if (Array.isArray(subItem)) {
            subItem.map((sItem, sIndex) => {
              sItem["position"] = `${index}-${subIndex}-${sIndex}`.toString();
            });
          } else {
            subItem["position"] = `${index}-${subIndex}`.toString();
          }
        });
      } else {
        item["position"] = index.toString();
      }
    });
    if (final[oldIndex][left] === undefined) {
      setPleaseSelect(final, oldIndex, left);
    }
    if (final[oldIndex][right] === undefined) {
      setPleaseSelect(final, oldIndex, right);
    }
    if (final[newIndex][left] === undefined) {
      setPleaseSelect(final, newIndex, left);
    }
    if (final[newIndex][right] === undefined) {
      setPleaseSelect(final, newIndex, right);
    }

    ruleDispatch({
      type: "UPDATE_RULE",
      updatedRule: final,
    });
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).replace("idx-", ""));
    const newIndex = Number(String(over.id).replace("idx-", ""));
    onSortEnd({ oldIndex, newIndex });
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rule.map((_, i) => `idx-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="tw-my-6">
            {rule.map((element, eleindex) => (
              <SortableItem
                key={`item-${element.position}`}
                id={`idx-${eleindex}`}
                ind={eleindex}
                element={element}
                rule={rule}
                disabled={element.type === "Operator"}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

const SortableItem = ({ id, element, ind, rule, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    zIndex: isDragging ? 1 : "auto",
  };

  // Drag handle bound to this item's drag activator — mirrors the old
  // SortableHandle so Condition can keep rendering <DragHandle /> unchanged.
  const DragHandle = () => (
    <i
      ref={setActivatorNodeRef}
      className="fas fa-bars"
      style={{ color: "var(--ds-ink-disabled, #adb5bd)", cursor: "grab" }}
      {...attributes}
      {...listeners}
    />
  );

  if (Array.isArray(element)) {
    //render a nested condition, it needs special handling
    return (
      <div ref={setNodeRef} style={style} className="p-0 m-0">
        {rule.length > 1 && <DragHandle />}

        <NestedCondition
          {...{
            index: ind,
            element,
            rootPosition: ind,
          }}
        />
      </div>
    );
  } else {
    switch (element.type) {
      case COND:
        return (
          <div ref={setNodeRef} style={style}>
            <Condition
              {...{
                index: ind,
                element,
                position: element.position,
                rootPosition: ind,
                DragHandle,
                rule,
              }}
            />
          </div>
        );
      case OP:
        return (
          <div ref={setNodeRef} style={style}>
            <div className="separator tw-mt-3" />
            <ConditionOperator
              {...{
                element,
                position: element.position,
              }}
            />

            <div className="separator tw-mb-3" />
          </div>
        );
      default:
        return null;
    }
  }
};

export default RuleBody;
